import { Select, RadioGroup, FieldWrapper, TextInput, TextArea } from '@components/shared/forms';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import { colord } from 'colord';
import React, { useState } from 'react';
import cn from 'classnames';
import { SortDownIcon, SortUpIcon } from '@viewthespace/components';
import { AdminMicrosite, CloudinaryImageParams } from '@root/types';
import FormSubmitter from '@components/shared/Admin/FormSubmitter';
import ColorPicker from './ColorPicker';
import FontPicker from './FontPicker';
import ImageUploader from './ImageUploader';

const SECTIONS = [
  'sitewide',
  'home',
  'nav',
  'footer',
  'features',
  'availabilities',
  'contact',
] as const;
export const STATUSES = [
  'onboarding',
  'ready_for_approval',
  'approved_domain_setup',
  'live',
  'inactive',
  'retired',
] as const;
type Section = (typeof SECTIONS)[number];

export default function FormFields({ microsite }: { microsite: AdminMicrosite }) {
  const { t } = useTranslation('admin');
  const { setFieldValue, values } = useFormikContext<Partial<AdminMicrosite>>();

  const changeFontColorWithColorChange = (fontAttribute: string) => (color: string) => {
    setFieldValue(fontAttribute, colord(color).isDark() ? '#FFFFFF' : '#000000');
  };

  const [openSections, setOpenSections] = useState<Section[]>([]);
  const setOpen = (section: Section) => setOpenSections([...openSections, section]);
  const setClosed = (section: Section) => setOpenSections(openSections.filter(s => s !== section));
  const getIsOpen = (section: Section) => openSections.includes(section);

  const anySectionsOpened = openSections.length > 0;

  return (
    <>
      <FieldWrapper name="previewUrl" labelText={t('microsite.previewUrl.title')}>
        <a href={microsite.previewUrl} target="_blank" rel="noreferrer">
          {microsite.previewUrl}
        </a>
      </FieldWrapper>
      <Select
        labelText={t('microsite.status.title')}
        name="status"
        options={STATUSES.map(status => ({
          value: status,
          label: t(`microsite.status.${status}`),
        }))}
        isClearable={false}
        containerClass="!mb-0"
      />
      <div className="flex justify-between">
        <h2 className="font-title-de-emphasis">{t('microsite.stylingOptions')}</h2>
        <button
          className="text-cta-primary-default underline"
          type="button"
          onClick={() => {
            if (anySectionsOpened) {
              setOpenSections([]);
            } else {
              setOpenSections([...SECTIONS]);
            }
          }}
        >
          {t(anySectionsOpened ? 'microsite.collapseAll' : 'microsite.expandAll')}
        </button>
      </div>

      <FieldSection
        title={t('microsite.sections.sitewide')}
        getIsOpen={getIsOpen}
        section="sitewide"
        setOpen={setOpen}
        setClosed={setClosed}
      >
        <FontPicker />
        <ColorPicker
          name="sectionHeadingColor"
          labelText={t('microsite.sectionHeadingColor.title')}
          data-testid="section-heading-color-picker"
        />
        <ColorPicker
          name="backgroundColor"
          labelText={t('microsite.backgroundColor.title')}
          onChange={changeFontColorWithColorChange('textColor')}
          data-testid="background-color-picker"
        />
      </FieldSection>

      <FieldSection
        title={t('microsite.sections.nav')}
        getIsOpen={getIsOpen}
        section="nav"
        setOpen={setOpen}
        setClosed={setClosed}
      >
        <ColorPicker
          name="headerBackgroundColor"
          labelText={t('microsite.headerBackgroundColor.title')}
          onChange={changeFontColorWithColorChange('headerTextColor')}
        />
        <ColorPicker
          name="headerBorderColor"
          labelText={t('microsite.headerBorderColor.title')}
          hasTransparentOption
        />
        <TextInput
          name="tenantPortalUrl"
          labelText={t('microsite.tenantPortalUrl.title')}
          containerClass="!mb-0"
        />
        <ImageUploader
          img={values.topNavLogo as CloudinaryImageParams | null}
          onChange={img => setFieldValue('topNavLogo', img)}
          modalTitle={t('microsite.topNavLogo.editModalTitle')}
          labelText={t('microsite.topNavLogo.title')}
          name="topNavLogo"
          alt={t('microsite.topNavLogo.title')}
        />
      </FieldSection>

      <FieldSection
        title={t('microsite.sections.footer')}
        getIsOpen={getIsOpen}
        section="footer"
        setOpen={setOpen}
        setClosed={setClosed}
      >
        <ColorPicker
          name="footerBackgroundColor"
          labelText={t('microsite.footerBackgroundColor.title')}
          onChange={changeFontColorWithColorChange('footerTextColor')}
        />
        <ColorPicker
          name="footerBorderColor"
          labelText={t('microsite.footerBorderColor.title')}
          hasTransparentOption
        />
        <ImageUploader
          img={values.footerLogo as CloudinaryImageParams | null}
          onChange={img => setFieldValue('footerLogo', img)}
          modalTitle={t('microsite.footerLogo.editModalTitle')}
          labelText={t('microsite.footerLogo.title')}
          name="footerLogo"
          alt={t('microsite.footerLogo.title')}
        />
        <TextInput
          name="footerLogoLink"
          labelText={t('microsite.footerLogoLink.title')}
          containerClass="!mb-0"
        />
        <TextInput name="facebookUrl" labelText={t('microsite.facebookUrl.title')} />
        <TextInput name="instagramUrl" labelText={t('microsite.instagramUrl.title')} />
        <TextInput name="linkedinUrl" labelText={t('microsite.linkedinUrl.title')} />
        <TextInput name="tiktokUrl" labelText={t('microsite.tiktokUrl.title')} />
        <TextInput name="twitterUrl" labelText={t('microsite.twitterUrl.title')} />
        <TextArea name="footerLeftText" labelText={t('microsite.footerLeftText.title')} />
        <TextArea name="footerRightText" labelText={t('microsite.footerRightText.title')} />
      </FieldSection>

      <FieldSection
        title={t('microsite.sections.home')}
        getIsOpen={getIsOpen}
        section="home"
        setOpen={setOpen}
        setClosed={setClosed}
      >
        <RadioGroup
          name="homeHeroImageStyle"
          options={[
            { value: 'video', label: t('microsite.homeHeroImageStyle.video') },
            { value: 'single-image', label: t('microsite.homeHeroImageStyle.singleImage') },
          ]}
          labelText={t('microsite.homeHeroImageStyle.title')}
          data-testid="home-hero-image-style-selector"
        />
        <RadioGroup
          options={[
            { value: 'left', label: t('microsite.homeAddressTextPlacement.left') },
            { value: 'right', label: t('microsite.homeAddressTextPlacement.right') },
          ]}
          name="homeAddressTextPlacement"
          labelText={t('microsite.homeAddressTextPlacement.title')}
          data-testid="home-address-text-placement-selector"
        />
        <ColorPicker
          name="homeBuildingInfoBackgroundColor"
          labelText={t('microsite.homeBuildingInfoBackgroundColor.title')}
          onChange={changeFontColorWithColorChange('homeBuildingInfoFontColor')}
        />
        <ColorPicker
          name="homeBuildingInfoButtonColor"
          labelText={t('microsite.homeBuildingInfoButtonColor.title')}
          onChange={changeFontColorWithColorChange('homeBuildingInfoButtonTextColor')}
          data-testid="button-color-picker"
        />
      </FieldSection>

      <FieldSection
        title={t('microsite.sections.features')}
        getIsOpen={getIsOpen}
        section="features"
        setOpen={setOpen}
        setClosed={setClosed}
      >
        <ColorPicker
          name="featuresAmenitiesBackgroundColor"
          labelText={t('microsite.featuresAmenitiesBackgroundColor.title')}
          onChange={changeFontColorWithColorChange('featuresAmenitiesTextColor')}
        />
        <ColorPicker
          name="featuresCertificationBorderColor"
          labelText={t('microsite.featuresCertificationBorderColor.title')}
          hasTransparentOption
        />
      </FieldSection>

      <FieldSection
        title={t('microsite.sections.availabilities')}
        getIsOpen={getIsOpen}
        section="availabilities"
        setOpen={setOpen}
        setClosed={setClosed}
      >
        <RadioGroup
          name="availabilitiesStyle"
          options={[
            { value: 'card', label: t('microsite.availabilitiesStyle.card') },
            { value: 'table', label: t('microsite.availabilitiesStyle.table') },
          ]}
          labelText={t('microsite.availabilitiesStyle.title')}
          containerClass="!mb-0"
          data-testid="home-hero-image-style-selector"
        />
        <ColorPicker
          name="availabilitiesBorderColor"
          labelText={t('microsite.availabilitiesBorderColor.title')}
          hasTransparentOption
        />
        <ColorPicker
          name="availabilitiesButtonColor"
          labelText={t('microsite.buttonColor')}
          onChange={changeFontColorWithColorChange('availabilitiesButtonTextColor')}
        />
      </FieldSection>

      <FieldSection
        title={t('microsite.sections.contact')}
        getIsOpen={getIsOpen}
        section="contact"
        setOpen={setOpen}
        setClosed={setClosed}
      >
        <ColorPicker
          name="contactsBackgroundColor"
          labelText={t('microsite.contactsBackgroundColor.title')}
          data-testid="contact-background-color-picker"
        />
        <ColorPicker
          name="contactsCardBorderColor"
          labelText={t('microsite.contactsCardBorderColor.title')}
          data-testid="contact-card-border-color-picker"
          hasTransparentOption
        />
      </FieldSection>
      <FormSubmitter showCancelButton includePlaceholder />
    </>
  );
}

const FieldSection = ({
  title,
  children = null,
  getIsOpen,
  setOpen,
  setClosed,
  section,
}: {
  title: string;
  children?: React.ReactNode;
  getIsOpen: (section: Section) => boolean;
  setOpen: (section: Section) => void;
  setClosed: (section: Section) => void;
  section: Section;
}) => {
  const isOpen = getIsOpen(section);
  const onClick = isOpen ? () => setClosed(section) : () => setOpen(section);
  return (
    <section className="flex border-[2px] border-solid border-black-020 bg-cta-tertiary-hover p-2">
      <div>
        <button type="button" onClick={onClick}>
          {isOpen ? <SortUpIcon /> : <SortDownIcon />}
        </button>
      </div>
      <div>
        <button type="button" onClick={onClick} className="mr-2">
          <h3 className="flex items-center font-subtitle">
            <span>{title}</span>
          </h3>
        </button>
        <div className={cn('hidden flex-col gap-2 pt-2', isOpen && '!flex')}>{children}</div>
      </div>
    </section>
  );
};
